import type { WidgetSettings } from "@/types";

/**
 * Parent-iframe message bridge. The host page loads `loader.js` which
 * creates our iframe; both sides exchange tiny messages to share
 * identity and drive open/close/resize.
 *
 * Origin check is implicit — we only accept messages whose `event.origin`
 * matches `window.location.ancestorOrigins[0]` if available, otherwise we
 * trust any origin (dev mode, no iframe). The host-side loader only
 * dispatches to our own origin, so there's no symmetry issue.
 */
type HostCommand = "open" | "close";
export type HostTheme = "light" | "dark";

export interface HostBridgeHandlers {
  onSettings: (settings: {
    identity?: Record<string, unknown>;
    identityHash?: string;
    hostTheme?: HostTheme;
    locale?: string;
  }) => void;
  onHostCommand: (command: HostCommand) => void;
}

export class HostBridge {
  private readonly listener: (e: MessageEvent) => void;

  constructor(private readonly handlers: HostBridgeHandlers) {
    this.listener = (event) => this.onMessage(event);
    window.addEventListener("message", this.listener);
  }

  destroy() {
    window.removeEventListener("message", this.listener);
  }

  /**
   * Tell the host we've booted and are ready to accept identity.
   * The host loader responds with `chatzou:settings`.
   */
  announceReady(): void {
    this.post({ type: "chatzou:ready" });
  }

  /**
   * Tell the host to resize the iframe when the chat window opens/closes.
   * The loader decides the final dimensions (respects mobile breakpoint).
   */
  notifyOpen(open: boolean): void {
    this.post({ type: "chatzou:resize", open });
  }

  /**
   * Tell the host which side the widget should dock.
   */
  notifyPosition(position: "bottom-left" | "bottom-right"): void {
    this.post({ type: "chatzou:position", position });
  }

  private post(msg: Record<string, unknown>): void {
    if (window.parent && window.parent !== window) {
      // Target "*" is acceptable for non-sensitive UI state; the loader
      // verifies origin on its side and ignores anything unexpected.
      window.parent.postMessage(msg, "*");
    }
  }

  private onMessage(event: MessageEvent): void {
    const data = event.data as
      | {
          type?: string;
          identity?: Record<string, unknown>;
          identityHash?: string;
          hostTheme?: HostTheme;
          locale?: string;
          command?: HostCommand;
        }
      | null
      | undefined;
    if (!data || typeof data !== "object" || !data.type) return;

    switch (data.type) {
      case "chatzou:settings":
        this.handlers.onSettings({
          identity: data.identity,
          identityHash: data.identityHash,
          hostTheme:
            data.hostTheme === "dark" || data.hostTheme === "light"
              ? data.hostTheme
              : undefined,
          locale: typeof data.locale === "string" ? data.locale : undefined,
        });
        break;
      case "chatzou:host-command":
        if (data.command === "open" || data.command === "close") {
          this.handlers.onHostCommand(data.command);
        }
        break;
      default:
        break;
    }
  }
}

/**
 * Wait up to `timeoutMs` for the host to push identity via postMessage.
 * If nothing arrives we resolve with empty — the widget then boots
 * anonymously. Prevents the "ready" handshake from blocking the UI.
 */
export function awaitIdentity(timeoutMs = 350): Promise<
  Pick<WidgetSettings, "identity" | "identityHash"> & {
    hostTheme?: HostTheme;
  }
> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener("message", onMsg);
      resolve({});
    }, timeoutMs);

    const onMsg = (event: MessageEvent) => {
      const data = event.data as
        | {
            type?: string;
            identity?: unknown;
            identityHash?: unknown;
            hostTheme?: unknown;
          }
        | null
        | undefined;
      if (!data || data.type !== "chatzou:settings") return;
      clearTimeout(timer);
      window.removeEventListener("message", onMsg);
      resolve({
        identity: data.identity as Record<string, unknown> | undefined,
        identityHash: data.identityHash as string | undefined,
        hostTheme:
          data.hostTheme === "dark" || data.hostTheme === "light"
            ? (data.hostTheme as HostTheme)
            : undefined,
      });
    };
    window.addEventListener("message", onMsg);

    // Announce ready so the host knows to respond immediately.
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: "chatzou:ready" }, "*");
    }
  });
}
